import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export type AddInstrumentsSessionOption = { id: number };
export type AddInstrumentsAssetOption = { id: number; ric: string | null };

const apiErrorMessage = (result: unknown, fallback: string): string => {
  if (typeof result === 'string' && result) return result;
  if (result && typeof result === 'object') {
    const messages = Object.values(result as Record<string, unknown>).flatMap(
      value => (Array.isArray(value) ? value : [value]),
    );
    if (messages.length) return messages.map(String).join(' ');
  }
  return fallback;
};

const hasOptionId = (value: unknown): value is AddInstrumentsSessionOption =>
  !!value &&
  typeof value === 'object' &&
  Number.isInteger((value as AddInstrumentsSessionOption).id) &&
  (value as AddInstrumentsSessionOption).id > 0;

const requestOptions = async <Option>(
  path: string,
  isOption: (value: unknown) => value is Option,
  fallback: string,
): Promise<Option[]> => {
  // Direct reads keep picker objects and loading/error state outside Redux.
  const { result, status } = await apiCaller<Record<string, never>>({
    path,
    method: 'GET',
    data: {},
    apiUrl: API_URL,
  });
  if (status.toString()[0] !== '2') {
    throw new Error(apiErrorMessage(result, fallback));
  }
  if (!Array.isArray(result) || !result.every(isOption)) {
    throw new Error(fallback);
  }
  return result;
};

export const fetchAddInstrumentsSessionOptions = () =>
  requestOptions(
    '/admin/add_instruments_session_options',
    hasOptionId,
    'Session options could not be loaded.',
  );

export const fetchAddInstrumentsAssetOptions = () =>
  requestOptions(
    '/admin/add_instruments_asset_options',
    (value): value is AddInstrumentsAssetOption =>
      hasOptionId(value) &&
      (typeof (value as AddInstrumentsAssetOption).ric === 'string' ||
        (value as AddInstrumentsAssetOption).ric === null),
    'Asset options could not be loaded.',
  );
